import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {SpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE, BLUE_LIGHT, BLUE_LIGHTEST, GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import MenuPopover from 'ui-library/menus/MenuPopover';

export const FUNCTION_HELP_COLLAPSED_PREFERENCE_KEY = 'notebook.function_help_collapsed';
export const FUNCTION_HELP_CLOSED_PREFERENCE_KEY = 'notebook.function_help_closed';

export default function FunctionHelpDropdown({
  spreadsheetFunction,
  currentParameterIndex,
}: {
  spreadsheetFunction: SpreadsheetFunction;
  currentParameterIndex: number;
}): JSX.Element {
  const {name, description, params, repeatableLastParamType, repeatableLastParamDescription} =
    spreadsheetFunction;

  const [isCollapsed, setIsCollapsed] = usePreference<boolean | undefined>(
    FUNCTION_HELP_COLLAPSED_PREFERENCE_KEY
  );

  const [isClosed, setIsClosed] = usePreference<boolean | undefined>(FUNCTION_HELP_CLOSED_PREFERENCE_KEY);

  const isRepeatableParam = (index: number): boolean =>
    index === params.length - 1 && Boolean(repeatableLastParamType);

  // Returns an array of <span>s and comma text elements for the given parameter name, with the parameter highlighted if it is the current parameter. If the parameter is repeatable, the returned array has more than one element.
  function formatParam(name: string, isOptional: boolean, index: number): Array<JSX.Element | string> {
    if (!isRepeatableParam(index)) {
      return [
        <span
          key={index}
          className={classNames(
            'functionHelp-param',
            css(styles.usageArg),
            currentParameterIndex === index && css(styles.usageArgHighlighted)
          )}
          {...getTestProps({component: 'FunctionHelpDropdown', element: 'param', index: String(index)})}
          data-amp-safe={false}
        >
          {isOptional ? `[${name}]` : name}
        </span>,
      ];
    } else if (isOptional) {
      // param is repeatable and optional
      // Use non-breaking space to ensure that this element doesn't get split up by word wrap
      return [
        <span
          key={index}
          className={classNames(
            'functionHelp-param',
            css(styles.usageArg),
            currentParameterIndex >= index && css(styles.usageArgHighlighted)
          )}
          {...getTestProps({component: 'FunctionHelpDropdown', element: 'param', index: String(index)})}
          data-amp-safe={false}
        >
          [{name},&nbsp;…]
        </span>,
      ];
    } else {
      // param is required, but additional params are optional and repeated
      return [
        // required 'name1'
        <span
          key={index}
          className={classNames(
            'functionHelp-param',
            css(styles.usageArg),
            currentParameterIndex === index && css(styles.usageArgHighlighted)
          )}
          {...getTestProps({component: 'FunctionHelpDropdown', element: 'param', index: String(index)})}
          data-amp-safe={false}
        >
          {name}1
        </span>,
        ', ',
        // optional '[name2, …]'
        <span
          key={index + 1}
          className={classNames(
            'functionHelp-param',
            css(styles.usageArg),
            currentParameterIndex > index && css(styles.usageArgHighlighted)
          )}
          {...getTestProps({
            component: 'FunctionHelpDropdown',
            element: 'param',
            index: String(index + 1),
          })}
          data-amp-safe={false}
        >
          [{name}2,&nbsp;…]
        </span>,
      ];
    }
  }

  let menuContent: JSX.Element;

  if (isClosed) {
    menuContent = (
      <div
        className={css(styles.menuClosedContent)}
        {...getTestProps({component: 'FunctionHelpDropdown', element: 'openButton'})}
        // Use onMouseDown with preventDefault instead of onClick to keep focus in the cell
        onMouseDown={(event) => {
          event.preventDefault();
          setIsClosed(false);
        }}
      >
        <BenchlingIcon title="Open function help" icon="question" className={css(styles.menuClosedIcon)} />
      </div>
    );
  } else {
    menuContent = (
      <>
        <div
          className={css(styles.usage)}
          {...getTestProps({component: 'FunctionHelpDropdown', element: 'usage'})}
          onMouseDown={(event) => {
            event.preventDefault();
            setIsCollapsed(!isCollapsed);
          }}
        >
          <BenchlingIcon
            title={isCollapsed ? 'Expand' : 'Collapse'}
            icon={isCollapsed ? 'chevron-down' : 'chevron-up'}
            className={classNames('u-small-spacing-right', css(styles.headerIcon))}
          />
          {name}(
          {
            // comma separated list of params
            params.flatMap(({name, optional}, index) => [
              ...formatParam(name, Boolean(optional), index),
              index < params.length - 1 && ', ',
            ])
          }
          )
          <div className="pull-right">
            <BenchlingIcon
              title="Close"
              icon="close"
              className={classNames('u-small-spacing-left', css(styles.headerIcon))}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation(); // Don't toggle is collapsed
                setIsClosed(true);
              }}
            />
          </div>
        </div>
        <div
          className={classNames('u-small-padding', isCollapsed && css(styles.collapsedDescription))}
          {...getTestProps({component: 'FunctionHelpDropdown', element: 'description'})}
        >
          <p className={css(styles.description)}>{description}</p>
          {params.length
            ? [
                params.map((param, index) => (
                  <div className="u-small-spacing-top" key={index}>
                    <div className={css(styles.paramName)}>
                      {param.name}
                      {isRepeatableParam(index) ? '1' : ''} {param.optional && '- [optional]'}
                    </div>
                    <span>{param.description}</span>
                  </div>
                )),
                repeatableLastParamType && (
                  <div className="u-small-spacing-top" key={params.length}>
                    <div className={css(styles.paramName)}>
                      {params[params.length - 1].name}2… - [optional] repeatable
                    </div>
                    <span>{repeatableLastParamDescription}</span>
                  </div>
                ),
              ]
            : undefined}
        </div>
      </>
    );
  }

  return (
    <MenuPopover
      dataTestAttrs={{component: 'FunctionHelpDropdown', element: 'root'}}
      className={classNames(
        'functionHelpDropdown-menu',
        css(styles.menu),
        isClosed && 'functionHelpDropdown-menu--closed',
        isClosed && css(styles.menuClosed)
      )}
    >
      {menuContent}
    </MenuPopover>
  );
}

export const styles = StyleSheet.create({
  menu: {
    width: 'max-content',
    maxWidth: 400,
  },
  menuClosed: {
    bottom: 'unset',
    left: '-15px',
    margin: 0,
  },
  menuClosedContent: {
    background: BLUE_LIGHT,
    color: WHITE,
    width: 12,
    textAlign: 'center',
    borderRadius: 3,
    cursor: 'pointer',
  },
  menuClosedIcon: {
    transform: 'scale(0.8)',
  },
  usage: {
    background: BLUE_LIGHTEST,
    fontWeight: 'bold',
    padding: 10,
    marginBottom: 0,
    fontFamily: FONT_FAMILY_MONOSPACED,
    color: BLUE,
    cursor: 'pointer',
  },
  usageArg: {
    borderRadius: 2,
    padding: '2px 0',
    transition: '0.2s ease-out',
  },
  usageArgHighlighted: {
    background: BLUE,
    color: WHITE,
    padding: '2px',
  },
  headerIcon: {
    // add extra top spacing to visually center the collapse, expand, and close icons
    marginTop: 2,
  },
  // Have to "hide" the description using maxHeight=0 instead of display=none because taking up horizontal space avoids the menu element width shrinking when the description is collapsed.
  collapsedDescription: {
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    overflowY: 'hidden',
  },
  description: {
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: GRAY_LIGHT,
  },
  paramName: {
    fontWeight: 'bold',
    fontFamily: FONT_FAMILY_MONOSPACED,
    color: BLUE,
  },
});
